// GET /api/share-candidates (Task 3.4) — the picker's fixed lists. Same
// booby-trap discipline as useCopyLinks.js/useForecastShares.js (arm-before-
// await, monotonic seq guard, aliveRef in its own mount-only effect).
//
// NOT FORECAST-SCOPED, unlike useForecastShares — this is "what can I share
// TO," which depends only on the caller's own org, not on which forecast the
// dialog happens to be open for. `enabled` gates the fetch so it only fires
// while the dialog is actually open (no reason to hit this endpoint on every
// app boot for a feature the owner may never use this session).
import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet } from "../lib/apiClient.js";
import { userFacingError } from "../lib/forecast/runErrorText.js";

/**
 * The share picker's candidate lists.
 *
 * @param {object} args
 * @param {boolean} [args.enabled] fetch only while true (e.g. the dialog is open)
 * @param {Function} [args.apiGetImpl] injectable for tests
 * @returns {{
 *   teams: Array<{slug: string, memberCount: number}>,
 *   people: Array<{userId: string, name: string|null, email: string}>,
 *   loading: boolean, error: string|null, refresh: () => Promise<boolean>,
 * }}
 */
export function useShareCandidates({ enabled = true, apiGetImpl = apiGet } = {}) {
  const [teams, setTeams] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Loading from the first render after it is enabled, not from the effect that fetches: a frame
  // painted in between would show the empty picker, then its loading line (as useForecastShares).
  const [loadingWhen, setLoadingWhen] = useState(enabled);
  if (enabled !== loadingWhen) {
    setLoadingWhen(enabled);
    setLoading(enabled);
  }

  const seqRef = useRef(0);
  const abortRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setTeams([]);
      setPeople([]);
      setLoading(false);
      return false;
    }
    const previous = abortRef.current;
    const ac = new AbortController();
    abortRef.current = ac;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    if (previous) previous.abort();
    setLoading(true);

    const r = await apiGetImpl("/api/share-candidates", { signal: ac.signal });

    if (!aliveRef.current || seq !== seqRef.current) return false;
    setLoading(false);
    if (!r.ok) {
      console.error("[Sayer] share candidates failed:", r.status, r.body);
      setError(
        userFacingError(
          r.body?.error,
          "Couldn't load the people and teams you can share with. Try again.",
          {
            status: r.status,
          },
        ),
      );
      return false;
    }
    setError(null);
    setTeams(Array.isArray(r.body?.teams) ? r.body.teams : []);
    setPeople(Array.isArray(r.body?.people) ? r.body.people : []);
    return true;
  }, [enabled, apiGetImpl]);

  useEffect(() => {
    // Same shape as useCopyLinks.js's mount effect (that file's own refresh
    // sets state synchronously too, on its early-return branch) — the
    // React-Compiler-based `react-hooks/set-state-in-effect` rule flags THIS
    // call site but not that one despite the identical structure, which is a
    // known heuristic quirk of that rule (cf. useCopyLinksGate.js's own note
    // on the same rule). The effect's whole job IS synchronizing this hook's
    // state with the server on mount/enabled-change — exactly the case the
    // rule's own docs carve out ("subscribe for updates... calling setState
    // when external state changes").
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
    return () => {
      const ac = abortRef.current;
      if (ac) ac.abort();
    };
  }, [refresh]);

  return { teams, people, loading, error, refresh };
}
